/* eslint-disable no-magic-numbers */
/* eslint-disable sort-imports */

import { BulletinItem } from 'src/components/bulletins/bulletinList/BulletinList';
import { BulletinsService } from 'src/services/bulletins.service';
import { ComboBox } from 'src/components/ui/combobox';
import { DatePicker } from 'src/components/date-picker';
import FooterPrivacy from 'src/views/auth/components/baseTemplate/FooterPrivacy';
import { getBrandByDealerNumber } from 'src/utils/helpers';
import { IUser } from 'src/utils/interfaces/user';
import Spinner from 'src/components/ui/spinner';
import Table, { BulletinTableSortColumn } from 'src/components/bulletins-table';
import TableFooter from 'src/components/table/pagination/tableFooter';
import { useLocalization } from 'src/utils/useLocalization';
import { useSelector } from 'react-redux';
import { useCallback, useEffect, useRef, useState } from 'react';
import { HttpStatusCode } from 'src/utils/enums/http-method.enum';
import {
    BULLETIN_TYPE_CODES,
    BulletinSortDirection,
    formatDateForApi,
    getDefaultDateRange,
    getErrorStatus,
    sortBulletins,
    sortByEndDate,
} from 'src/utils/bulletins';

import './styles.scss';

const EMPTY_RESULTS = 0;
const DEFAULT_PAGE_SIZE = 10;
const INITIAL_FILTER_RESET_KEY = 0;
const RESET_KEY_INCREMENT = 1;
const NOT_FOUND_STATUS = 404;

type StatusFilter = 'All' | 'Active' | 'Expired';

type SearchField = 'title' | 'bulletinNumber';

interface BulletinFilters {
    searchInput: string;
    statusFilter: StatusFilter;
    typeFilter: string;
    startDate: string;
    endDate: string;
}

const getDefaultFilters = (range: {
    startDate: string;
    endDate: string;
}): BulletinFilters => ({
    searchInput: '',
    statusFilter: 'Active',
    typeFilter: '',
    startDate: range.startDate,
    endDate: range.endDate,
});

const BulletinsPage = () => {
    const { bulletins, labelText } = useLocalization();

    const bulletinTypeOptions = [
        {
            code: 'Informational',
            label: bulletins.LABEL_BULLETINS_TYPE_INFORMATIONAL,
        },
        {
            code: 'Programs',
            label: bulletins.LABEL_BULLETINS_TYPE_PROGRAMS,
        },
    ];

    const currentDealers = useSelector((state: IUser) => state.currentDealers);

    const dealerDataInfo = useSelector((state: IUser) => state.dealerDataInfo);

    const [defaultDateRange] = useState(getDefaultDateRange);

    const [isLoading, setIsLoading] = useState(true);

    const [bulletinDataset, setBulletinDataSet] = useState<BulletinItem[]>([]);

    const [displayedContent, setDisplayedContent] = useState<BulletinItem[]>(
        [],
    );

    /*
     * Filter/form state only.
     *
     * Changing these values DOES NOT call the backend.
     */
    const [searchInput, setSearchInput] = useState('');

    const [statusFilter, setStatusFilter] = useState<StatusFilter>('Active');

    const [typeFilter, setTypeFilter] = useState('');

    const [startDate, setStartDate] = useState(defaultDateRange.startDate);

    const [endDate, setEndDate] = useState(defaultDateRange.endDate);

    const [filterResetKey, setFilterResetKey] = useState(
        INITIAL_FILTER_RESET_KEY,
    );

    /*
     * Requirement:
     * End Date ascending by default.
     */
    const [sortColumn, setSortColumn] =
        useState<BulletinTableSortColumn>('endDate');

    const [sortDirection, setSortDirection] =
        useState<BulletinSortDirection>('asc');

    const dealerId = currentDealers?.default as string | undefined;

    const dealerBrand = dealerDataInfo?.dealer?.brand;

    const dealerNumber = dealerDataInfo?.dealer?.dealerNumber;

    const region = dealerDataInfo?.dealer?.region?.regionCode;

    const district = dealerDataInfo?.dealer?.district?.districtCode;

    const brand =
        dealerDataInfo && dealerBrand != null && dealerNumber
            ? getBrandByDealerNumber(dealerBrand, dealerNumber)
            : 'vw6';

    const mountedRef = useRef(false);

    /*
     * Prevent stale responses from replacing
     * the result of a newer search.
     */
    const latestRequestRef = useRef(0);

    /*
     * Avoid duplicate identical in-flight requests.
     */
    const inFlightRef = useRef(new Map<string, Promise<BulletinItem[]>>());

    useEffect(() => {
        mountedRef.current = true;

        return () => {
            mountedRef.current = false;
            latestRequestRef.current += 1;
        };
    }, []);

    const fetchBulletins = useCallback(
        async (filters: BulletinFilters) => {
            const requestId = ++latestRequestRef.current;

            if (
                !dealerId ||
                dealerBrand == null ||
                region == null ||
                district == null
            ) {
                setBulletinDataSet([]);
                setDisplayedContent([]);
                setIsLoading(false);

                return;
            }

            setIsLoading(true);

            const searchTypes: Array<'ACTIVE' | 'EXPIRED'> =
                filters.statusFilter === 'Active'
                    ? ['ACTIVE']
                    : filters.statusFilter === 'Expired'
                    ? ['EXPIRED']
                    : ['ACTIVE', 'EXPIRED'];

            const trimmedSearch = filters.searchInput.trim();

            /*
             * Search text must be checked against both
             * Title and Bulletin Number.
             */
            const searchFields: SearchField[] = trimmedSearch
                ? ['title', 'bulletinNumber']
                : ['title'];

            const bulletinType = !filters.typeFilter
                ? null
                : BULLETIN_TYPE_CODES[filters.typeFilter];

            const requestStartDate =
                filters.startDate || defaultDateRange.startDate;

            const requestEndDate = filters.endDate || defaultDateRange.endDate;

            const requestKey = JSON.stringify([
                dealerId,
                dealerBrand,
                region,
                district,
                searchTypes,
                searchFields,
                trimmedSearch,
                bulletinType,
                requestStartDate,
                requestEndDate,
            ]);

            try {
                let pending = inFlightRef.current.get(requestKey);

                if (!pending) {
                    const service = new BulletinsService();

                    const requests = searchTypes.flatMap((searchType) =>
                        searchFields.map((searchField) =>
                            service.getBulletinListDealer({
                                dealerId,
                                brand: dealerBrand,
                                region,
                                district,
                                searchType,

                                startDate: formatDateForApi(requestStartDate),

                                endDate: formatDateForApi(requestEndDate),

                                title:
                                    searchField === 'title'
                                        ? trimmedSearch || null
                                        : null,

                                bulletinNumber:
                                    searchField === 'bulletinNumber'
                                        ? trimmedSearch || null
                                        : null,

                                bulletinType,
                            }),
                        ),
                    );

                    /*
                     * Do not use Promise.all here.
                     *
                     * A 404 from one search combination must not
                     * remove data returned by another combination.
                     *
                     * Example:
                     *
                     * ACTIVE/title -> 404
                     * ACTIVE/number -> 200
                     * EXPIRED/title -> 404
                     * EXPIRED/number -> 200
                     *
                     * We must return both successful result sets.
                     */
                    pending = Promise.allSettled(requests).then((responses) => {
                        const results: BulletinItem[] = [];

                        let realError: unknown | undefined;

                        responses.forEach((response) => {
                            if (response.status === 'fulfilled') {
                                const apiResponse = response.value;

                                if (
                                    apiResponse.status ===
                                    HttpStatusCode.SUCCESS
                                ) {
                                    if (Array.isArray(apiResponse.data)) {
                                        results.push(
                                            ...(apiResponse.data as BulletinItem[]),
                                        );
                                    } else {
                                        realError ??= new Error(
                                            'Invalid bulletin response',
                                        );
                                    }

                                    return;
                                }

                                /*
                                 * A fulfilled 404 represents
                                 * no results for only this request.
                                 */
                                if (apiResponse.status === NOT_FOUND_STATUS) {
                                    return;
                                }

                                realError ??= new Error(
                                    `Unable to retrieve bulletins. Status: ${apiResponse.status}`,
                                );

                                return;
                            }

                            /*
                             * Axios usually rejects 404,
                             * so it commonly arrives here.
                             */
                            const status = getErrorStatus(response.reason);

                            if (status === NOT_FOUND_STATUS) {
                                return;
                            }

                            realError ??= response.reason;
                        });

                        /*
                         * 401 / 403 / 500 / network errors
                         * are actual search failures.
                         */
                        if (realError !== undefined) {
                            throw realError;
                        }

                        /*
                         * Remove duplicates because Title and
                         * Bulletin # searches can find the same item.
                         */
                        const uniqueResults = Array.from(
                            new Map(
                                results.map((item) => [item.documentId, item]),
                            ).values(),
                        );

                        /*
                         * Default ordering:
                         * bulletin expiring first appears first.
                         */
                        return sortByEndDate(uniqueResults, true);
                    });

                    inFlightRef.current.set(requestKey, pending);

                    const removePending = () => {
                        if (inFlightRef.current.get(requestKey) === pending) {
                            inFlightRef.current.delete(requestKey);
                        }
                    };

                    void pending.then(removePending, removePending);
                }

                const results = await pending;

                if (
                    mountedRef.current &&
                    requestId === latestRequestRef.current
                ) {
                    setSortColumn('endDate');

                    setSortDirection('asc');

                    setDisplayedContent([]);

                    setBulletinDataSet(results);
                }
            } catch (error) {
                if (
                    mountedRef.current &&
                    requestId === latestRequestRef.current
                ) {
                    console.error('Unable to retrieve bulletins', error);

                    setDisplayedContent([]);
                    setBulletinDataSet([]);
                }
            } finally {
                if (
                    mountedRef.current &&
                    requestId === latestRequestRef.current
                ) {
                    setIsLoading(false);
                }
            }
        },
        [
            dealerId,
            dealerBrand,
            region,
            district,
            defaultDateRange.startDate,
            defaultDateRange.endDate,
        ],
    );

    /*
     * Initial page load.
     *
     * Runs on mount; Clear Filters repeats the same request.
     */
    useEffect(() => {
        void fetchBulletins(getDefaultFilters(defaultDateRange));
    }, [fetchBulletins, defaultDateRange.startDate, defaultDateRange.endDate]);

    /*
     * Every control re-runs the search with ALL the filters currently
     * applied; only the changed ones are overridden.
     *
     * Reads the latest values from a ref so the callbacks stay stable:
     * DatePicker re-initializes itself when its handler changes.
     */
    const latestFiltersRef = useRef<BulletinFilters>(
        getDefaultFilters(defaultDateRange),
    );

    latestFiltersRef.current = {
        searchInput,
        statusFilter,
        typeFilter,
        startDate,
        endDate,
    };

    const applyFilters = useCallback(
        (changes: Partial<BulletinFilters> = {}) => {
            void fetchBulletins({ ...latestFiltersRef.current, ...changes });
        },
        [fetchBulletins],
    );

    const handleStatusSelect = (value: string) => {
        const nextStatus: StatusFilter =
            value === bulletins.LABEL_BULLETINS_FILTER_ACTIVE
                ? 'Active'
                : value === bulletins.LABEL_BULLETINS_FILTER_EXPIRED
                ? 'Expired'
                : 'All';

        if (nextStatus === statusFilter) {
            return;
        }

        setStatusFilter(nextStatus);

        applyFilters({ statusFilter: nextStatus });
    };

    const handleTypeSelect = (value: string) => {
        const nextType =
            value === bulletins.LABEL_BULLETINS_FILTER_ALL
                ? ''
                : bulletinTypeOptions.find((option) => option.label === value)
                      ?.code ?? '';

        if (nextType === typeFilter) {
            return;
        }

        setTypeFilter(nextType);

        applyFilters({ typeFilter: nextType });
    };

    const handleDateRangeClose = useCallback(
        (nextStartDate: string, nextEndDate: string) => {
            const resolvedStartDate =
                nextStartDate || defaultDateRange.startDate;

            const resolvedEndDate = nextEndDate || defaultDateRange.endDate;

            if (
                resolvedStartDate === latestFiltersRef.current.startDate &&
                resolvedEndDate === latestFiltersRef.current.endDate
            ) {
                return;
            }

            setStartDate(resolvedStartDate);

            setEndDate(resolvedEndDate);

            applyFilters({
                startDate: resolvedStartDate,
                endDate: resolvedEndDate,
            });
        },
        [defaultDateRange.startDate, defaultDateRange.endDate, applyFilters],
    );

    const handleSearchKeyDown = (
        event: React.KeyboardEvent<HTMLInputElement>,
    ) => {
        if (event.key === 'Enter') {
            applyFilters();
        }
    };

    const clearSearchText = () => {
        setSearchInput('');

        applyFilters({ searchInput: '' });
    };

    /*
     * Reset the form and reload the default list.
     */
    const clearFilters = () => {
        setSearchInput('');
        setStatusFilter('Active');
        setTypeFilter('');

        setStartDate(defaultDateRange.startDate);

        setEndDate(defaultDateRange.endDate);

        setFilterResetKey((currentKey) => currentKey + RESET_KEY_INCREMENT);

        void fetchBulletins(getDefaultFilters(defaultDateRange));
    };

    /*
     * Local sort only.
     * No API request.
     */
    const handleSort = (
        column: BulletinTableSortColumn,
        direction: BulletinSortDirection = 'asc',
    ) => {
        const nextDirection: BulletinSortDirection =
            column === sortColumn
                ? sortDirection === 'asc'
                    ? 'desc'
                    : 'asc'
                : direction;

        setSortColumn(column);

        setSortDirection(nextDirection);

        setBulletinDataSet((currentItems) =>
            sortBulletins(currentItems, column, nextDirection),
        );
    };

    return (
        <>
            <div className='bulletins-wrapper'>
                <div className='page-title'>
                    <h5>{bulletins.LABEL_BULLETINS_HEADER}</h5>
                </div>

                <div className='bulletins-search-panel'>
                    <h6>{bulletins.LABEL_BULLETINS_SEARCH}</h6>

                    <p>{bulletins.LABEL_BULLETINS_SEARCH_DESCRIPTION}</p>

                    <div className='bulletins-search-row'>
                        <div className='c-input search-text bulletins-search-input'>
                            <span id='searchIcon'>
                                <i
                                    className='c-icon c-icon--[semantic-search]'
                                    aria-hidden='true'
                                    role='img'
                                />
                            </span>

                            <input
                                aria-label={
                                    bulletins.LABEL_BULLETINS_SEARCH_PLACEHOLDER
                                }
                                className='c-input__input'
                                placeholder={
                                    bulletins.LABEL_BULLETINS_SEARCH_PLACEHOLDER
                                }
                                value={searchInput}
                                onChange={(event) =>
                                    setSearchInput(event.target.value)
                                }
                                onKeyDown={handleSearchKeyDown}
                            />

                            {searchInput && (
                                <button
                                    className='bulletins-search-clear'
                                    type='button'
                                    aria-label={
                                        bulletins.LABEL_BULLETINS_CLEAR_SEARCH
                                    }
                                    onClick={clearSearchText}
                                >
                                    <i
                                        className='c-icon c-icon--[semantic-close]'
                                        aria-hidden='true'
                                    />
                                </button>
                            )}
                        </div>

                        <label>
                            <span>{bulletins.LABEL_BULLETINS_STATUS}</span>

                            <ComboBox
                                key={`status-${filterResetKey}`}
                                name='bulletin-status'
                                options={[
                                    bulletins.LABEL_BULLETINS_FILTER_ALL,
                                    bulletins.LABEL_BULLETINS_FILTER_ACTIVE,
                                    bulletins.LABEL_BULLETINS_FILTER_EXPIRED,
                                ]}
                                selectedOption={
                                    statusFilter === 'Active'
                                        ? bulletins.LABEL_BULLETINS_FILTER_ACTIVE
                                        : statusFilter === 'Expired'
                                        ? bulletins.LABEL_BULLETINS_FILTER_EXPIRED
                                        : bulletins.LABEL_BULLETINS_FILTER_ALL
                                }
                                onSelect={handleStatusSelect}
                            />
                        </label>

                        <label>
                            <span>{bulletins.LABEL_BULLETINS_TYPE}</span>

                            <ComboBox
                                key={`type-${filterResetKey}`}
                                name='bulletin-type'
                                options={[
                                    bulletins.LABEL_BULLETINS_FILTER_ALL,
                                    ...bulletinTypeOptions.map(
                                        (option) => option.label,
                                    ),
                                ]}
                                selectedOption={
                                    !typeFilter
                                        ? bulletins.LABEL_BULLETINS_FILTER_ALL
                                        : bulletinTypeOptions.find(
                                              (option) =>
                                                  option.code === typeFilter,
                                          )?.label ??
                                          bulletins.LABEL_BULLETINS_FILTER_ALL
                                }
                                onSelect={handleTypeSelect}
                            />
                        </label>

                        <label className='bulletins-date-range date-picker-container'>
                            <span>{bulletins.LABEL_BULLETINS_DATE_RANGE}</span>

                            <DatePicker
                                key={`date-range-${filterResetKey}`}
                                startDate={startDate}
                                endDate={endDate}
                                handleOnClose={handleDateRangeClose}
                                unrestrictedDates
                            />
                        </label>

                        <button
                            className='c-btn c-btn--link'
                            type='button'
                            onClick={clearFilters}
                        >
                            {bulletins.LABEL_BULLETINS_CLEAR_FILTERS}
                        </button>
                    </div>
                </div>

                <div
                    className={`main-container ${
                        isLoading ? 'no-content' : ''
                    }`}
                >
                    {isLoading ? (
                        <div className='loader_container'>
                            <Spinner />
                        </div>
                    ) : bulletinDataset.length === EMPTY_RESULTS ? (
                        <div className='bulletins-empty-state'>
                            {labelText.LABEL_TEXT_DASH_NORESULTS}
                        </div>
                    ) : (
                        <>
                            <Table
                                items={displayedContent}
                                sortColumn={sortColumn}
                                sortDirection={sortDirection}
                                onSort={handleSort}
                            />

                            <TableFooter
                                brand={brand}
                                content={bulletinDataset}
                                setDisplayedContent={setDisplayedContent}
                                defaultPerPage={DEFAULT_PAGE_SIZE}
                            />
                        </>
                    )}
                </div>
            </div>

            <div
                className='bulletins-wrapper'
                style={{
                    marginTop: 'auto',
                }}
            >
                <div
                    style={{
                        gridColumnStart: 2,
                        marginTop: '40px',
                    }}
                >
                    <FooterPrivacy />
                </div>
            </div>
        </>
    );
};

export default BulletinsPage;
